import { useRef, useState } from 'react'
import { Container, Row, Col } from '@ui/wrapper'
import {
  SectionWrapper,
  StyledText,
  StyledText2,
  OwnCol,
  ImgDiv,
  GlobalStyle,
  SwiperWrapper,
  SlideItemWrapper,
  SlideItemWrapperFull,
  ButtonWrapper
} from './style'
import SwiperSlider, { SwiperSlide } from '@ui/swiper'
import Image from '@ui/image'
import Modal from 'react-bootstrap/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import { faX } from '@fortawesome/pro-duotone-svg-icons/faX'

// Swiper's loop mode needs enough real slides to duplicate around the
// visible area; with `slidesPerView: 'auto'` + `centeredSlides`, fewer
// slides than this breaks the loop (Swiper logs "not enough slides for
// loop mode" and scrolling dead-ends in blank space).
const MIN_SLIDES_FOR_LOOP = 6
// The fullscreen viewer uses `slidesPerView: 1`, a much lower bar for
// Swiper's loop requirement than the preview's `'auto'` mode above.
const MIN_SLIDES_FOR_FULLSCREEN_LOOP = 3

const BSWForumArea = ({ data, data2, dataSlider, ...rest }) => {
  const [show, setShow] = useState(false)
  const [clickedSlideIndex, setClickedSlideIndex] = useState(0)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(1)

  const swiperRef = useRef(null)

  if (
    !data.section_title &&
    !data.items?.length &&
    !data2.texts?.length &&
    !dataSlider?.images?.length
  ) {
    return null
  }

  const slideImages = dataSlider?.images?.filter((image) => image?.src) || []
  const slideCount = slideImages.length
  const canLoop = slideCount >= MIN_SLIDES_FOR_LOOP
  const canLoopFullscreen = slideCount >= MIN_SLIDES_FOR_FULLSCREEN_LOOP

  const handleClose = () => {
    setShow(false)
    if (canLoop) {
      swiperRef.current.autoplay.start()
    }
  }

  const openSlide = (index) => {
    swiperRef.current?.autoplay?.stop()
    setClickedSlideIndex(index)
    setShow(true)
  }

  const sliderAutoplaySpeed = 1000

  const slider = {
    slidesPerView: 'auto',
    centeredSlides: true,
    loop: canLoop,
    speed: 1000,
    spaceBetween: -1,
    autoplay: canLoop
      ? {
          delay: sliderAutoplaySpeed
        }
      : false,
    mousewheel: false,
    keyboard: false,
    onClick: (swiper) => {
      if (!swiper.clickedSlide) return

      const datasetIndex = swiper.clickedSlide.dataset.swiperSlideIndex
      openSlide(
        datasetIndex !== undefined ? Number(datasetIndex) : swiper.clickedIndex
      )
    },
    onInit: (swiper) => {
      swiperRef.current = swiper
      swiper.params.navigation.nextEl = '.next-button'
      swiper.navigation.destroy()
      swiper.navigation.init()
      swiper.navigation.update()
      if (canLoop) {
        // With `slidesPerView: 'auto'`, whether Swiper actually has
        // enough real slide width to loop around the visible area
        // depends on the measured container/slide geometry at init
        // time, not just the slide count. Swiper 12 no longer clones
        // slides for loop mode (there's no `.swiper-slide-duplicate`
        // to check for), so replicate its own eligibility formula —
        // the measured `slidesPerViewDynamic()` plus the `loopedSlides`
        // margin it requires for centered slides — to verify loop
        // actually engaged rather than trusting the threshold alone.
        const measuredSlidesPerView = swiper.slidesPerViewDynamic()
        const loopedSlidesNeeded = Math.max(
          1,
          Math.ceil(measuredSlidesPerView / 2)
        )
        const loopSucceeded =
          swiper.slides.length >= measuredSlidesPerView + loopedSlidesNeeded

        if (loopSucceeded) {
          setTimeout(() => {
            swiper.slideToLoop(1, sliderAutoplaySpeed)
          }, sliderAutoplaySpeed)
        } else {
          swiper.params.loop = false
          swiper.loopDestroy()
        }
      }
    },
    pagination: false,
    navigation: {
      prevEl: '.prev-button',
      nextEl: '.next-button'
    }
  }

  const sliderFull = {
    slidesPerView: 1,
    centeredSlides: true,
    loop: canLoopFullscreen,
    speed: 1000,
    spaceBetween: 0,
    autoplay: false,
    onInit: (swiper) => {
      swiper.slideToLoop(clickedSlideIndex, 0, false)
      swiper.params.navigation.prevEl = '.prev-button'
      swiper.params.navigation.nextEl = '.next-button'
      swiper.navigation.destroy()
      swiper.navigation.init()
      swiper.navigation.update()
    },
    onSlideChange: (swiper) => {
      setCurrentSlideIndex(swiper.realIndex + 1)
    },
    pagination: false,
    navigation: {
      prevEl: '.prev-button',
      nextEl: '.next-button'
    }
  }

  return (
    <>
      <GlobalStyle />
      {slideCount > 0 && (
        <SwiperWrapper
          className="own-swiper-slide"
          style={{
            background: 'white',
            paddingTop: '30px',
            paddingBottom: '30px'
          }}
        >
          <SwiperSlider
            options={slider}
            prevIcon="fa fa-arrow-circle-left"
            opacityStyle
          >
            {slideImages.map((image, index) => (
              <SwiperSlide className="item" key={`image-${index}`}>
                <SlideItemWrapper
                  as="button"
                  type="button"
                  aria-label={
                    image.src?.alternativeText
                      ? `${image.src.alternativeText} vergrößern`
                      : 'Bild vergrößern'
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openSlide(index)
                    }
                  }}
                  width={(image.src.width * 300) / image.src.height}
                >
                  <Image
                    src={image.src.localFile || image.src}
                    alt={image.src?.alternativeText || ''}
                    sizes="50vw"
                  />
                </SlideItemWrapper>
              </SwiperSlide>
            ))}
            {slideCount >= 2 && (
              <>
                <FontAwesomeIcon
                  onClick={() => {
                    const currentIndex =
                      swiperRef.current.realIndex - 1 < 0
                        ? slideCount - 1
                        : swiperRef.current.realIndex - 1
                    if (!swiperRef.current.animating) {
                      swiperRef.current.slideToLoop(currentIndex, 1000, true)
                    }
                  }}
                  style={{
                    borderRadius: '50%',
                    color: 'white',
                    width: '30px',
                    height: '30px',
                    padding: '10px'
                  }}
                  className="swiper-button-prev prev-button own-background"
                  icon={faChevronLeft}
                />
                <FontAwesomeIcon
                  style={{
                    borderRadius: '50%',
                    color: 'white',
                    width: '30px',
                    height: '30px',
                    padding: '10px'
                  }}
                  className="swiper-button-next next-button own-background"
                  icon={faChevronRight}
                />
              </>
            )}
          </SwiperSlider>
        </SwiperWrapper>
      )}
      <SectionWrapper {...rest}>
        <Container>
          {data?.section_title && (
            <StyledText>
              <h4>{data.section_title?.title}</h4>
              {data?.buttons[0]?.path && (
                <a
                  style={{
                    marginTop: 20,
                    marginBottom: 10,
                    padding: '10px 20px 10px',
                    borderColor: 'rgb(181 90 48 / 0%)'
                  }}
                  href={data.buttons[0].path}
                  className="btn btn-outline-primary"
                >
                  {data.buttons[0].content}
                </a>
              )}
            </StyledText>
          )}

          {data?.items?.map((item, index) => (
            <StyledText
              key={index}
              style={{ background: 'white', marginBottom: '20px' }}
            >
              <Row gutters={{ lg: 60 }}>
                <Col
                  lg={12}
                  style={{
                    fontSize: 16,
                    paddingTop: 20,
                    paddingLeft: 60,
                    paddingBottom: item?.texts[1]?.content ? null : 20
                  }}
                >
                  {item?.name}
                </Col>
                {item?.title && (
                  <Col
                    lg={12}
                    style={{
                      paddingBottom: 20,
                      fontSize: 22,
                      paddingTop: 10,
                      paddingLeft: 60,
                      paddingRight: 60,
                      lineHeight: '1.3em'
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: item?.title }} />
                  </Col>
                )}
                {item?.texts?.map((text) => (
                  <OwnCol
                    lg={6}
                    style={{ paddingLeft: 60, paddingRight: 60 }}
                    key={text.id}
                    mb="30px"
                  >
                    {text.media && (
                      <ImgDiv>
                        <img
                          style={{
                            width: '100px',
                            height: '100px',
                            minWidth: '100px',
                            borderRadius: '50%'
                          }}
                          src={text.media.localFile.publicURL}
                        />
                      </ImgDiv>
                    )}
                    {text?.content && (
                      <div
                        dangerouslySetInnerHTML={{ __html: text.content }}
                        style={{ flex: 1, fontSize: text.media?.src ? 16 : 22 }}
                      />
                    )}
                  </OwnCol>
                ))}
              </Row>
            </StyledText>
          ))}

          {data2.texts && (
            <StyledText2
              style={{
                background: 'white',
                marginBottom: '20px',
                marginTop: '40px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '50px'
              }}
            >
              {data2?.texts[1]?.content && (
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: '15px',
                    fontSize: '24px'
                  }}
                  dangerouslySetInnerHTML={{ __html: data2.texts[1]?.content }}
                />
              )}
              {data2?.texts[0]?.media && (
                <ImgDiv style={{ marginBottom: '15px', marginRight: '0px' }}>
                  <img
                    style={{
                      width: '100px',
                      height: '100px',
                      minWidth: '100px',
                      borderRadius: '50%'
                    }}
                    src={data2?.texts[0]?.media.localFile.publicURL}
                  />
                </ImgDiv>
              )}
              {data2?.texts[0]?.content && (
                <div
                  style={{ textAlign: 'center' }}
                  dangerouslySetInnerHTML={{ __html: data2.texts[0]?.content }}
                />
              )}
              {data2?.texts[2]?.content && (
                <div
                  style={{ textAlign: 'center', marginTop: '15px' }}
                  dangerouslySetInnerHTML={{ __html: data2.texts[2]?.content }}
                />
              )}
              {data2?.buttons[0]?.content && (
                <a
                  style={{
                    marginTop: 25,
                    padding: '10px 40px 10px',
                    borderColor: 'rgb(181 90 48 / 0%)'
                  }}
                  href={data2.buttons[0].path}
                  className="btn btn-outline-primary"
                >
                  {data2.buttons[0]?.content}
                </a>
              )}
            </StyledText2>
          )}
        </Container>
        <Modal
          fullscreen
          show={show}
          onHide={handleClose}
          className="custom-modal"
        >
          <Modal.Body style={{ overflowX: 'auto' }}>
            <div style={{ color: 'white', padding: '10px' }}>
              {currentSlideIndex} / {slideCount}
            </div>
            <SwiperWrapper
              style={{ width: '100%' }}
              className="own-swiper-slide"
            >
              <SwiperSlider
                options={sliderFull}
                paginationTop="20px"
                opacityStyle
              >
                {slideImages.map((image, index) => (
                  <SwiperSlide className="item" key={`image-${index}`}>
                    <SlideItemWrapperFull>
                      <Image
                        src={image.src.localFile || image.src}
                        alt={image.src?.alternativeText || ''}
                        loading="lazy"
                        sizes="(max-width: 1000px) 100vw, 1920px"
                      />
                    </SlideItemWrapperFull>
                  </SwiperSlide>
                ))}
                {slideCount >= 2 && (
                  <>
                    <FontAwesomeIcon
                      style={{
                        borderRadius: '50%',
                        color: 'white',
                        width: '30px',
                        height: '30px',
                        padding: '10px'
                      }}
                      className="swiper-button-prev prev-button own-background"
                      icon={faChevronLeft}
                    />
                    <FontAwesomeIcon
                      style={{
                        borderRadius: '50%',
                        color: 'white',
                        width: '30px',
                        height: '30px',
                        padding: '10px'
                      }}
                      className="swiper-button-next next-button own-background"
                      icon={faChevronRight}
                    />
                  </>
                )}
              </SwiperSlider>
            </SwiperWrapper>
            <ButtonWrapper
              onClick={handleClose}
              type="button"
              aria-label="Schließen"
            >
              <FontAwesomeIcon icon={faX} />
            </ButtonWrapper>
          </Modal.Body>
        </Modal>
      </SectionWrapper>
    </>
  )
}

export default BSWForumArea
